import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import type { Account, CustomerDueDiligenceCase } from '../../../../types/management-auth';

import { useActions } from '../../../../Actions';
import { payoutInfoSelector } from '../../../../payout/merchant/config/selectors';
import { namespace, CustomerDueDiligenceQuery } from '../../../cddActions';
import { distances } from '../../../../styles/constants';

import { H2, P } from '../../../../components/Typography';
import Hr from '../../../../components/Hr';
import { Button, ButtonExternalLink } from '../../../../components/Buttons';
import useEffectCallback from '../../../../util/useEffectCallback';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { LoadingOverlay } from '../../../../components/Loading';
import FinishAccountIcon from '../FinishAccountIcon';
import BankAccounts from '../../../components/PayoutBankAccount/BankAccounts';
import ApplyForPayoutDialog from './ApplyForPayoutDialog';
import { useUrlSearchProperty } from '../../../../util/useUrlSearchProperty';
import { useAccountIds } from '../../../../auth/accessToken/withAccountIds';
import Card from '../../../../components/Card';

export type PayoutProps = {
    account: Account;
};

const getContractUrl = (cddCase: CustomerDueDiligenceCase | undefined) => {
    const event = (cddCase?.events || []).find((x) => x.event === 'case_created');
    return event?.created?.contract_url;
};

const ContractUrl = (
    {
        cddCase,
    }: {
        cddCase: CustomerDueDiligenceCase | undefined;
    }
) => {
    const { t } = useTranslation();
    const url = getContractUrl(cddCase);
    return (
        <>
            <Center>
                {url ? (

                    <ButtonExternalLink className="alt" href={url} target="_blank">
                        {t('settings.account.payout.application_link')}
                    </ButtonExternalLink>
                ) : (
                    <P>
                        {t('settings.account.payout.application_link_missing')}
                    </P>
                )}
            </Center>
        </>
    );
};

const enableAccountCddListParams: CustomerDueDiligenceQuery = { actions_on_approval: ['enable_payout_account']};

const PayoutInfo = (
    {
        account,
    }: {
        account: Account;
    }
) => {
    const accountIds = useAccountIds();
    const { getCddCases } = useActions(namespace);
    const getPayoutAccountConfig = useActions('payout.config').getAccountConfig;
    const {  loading, hasPayout } = useSelector(payoutInfoSelector(accountIds.optimisticProdAccountId));

    useEffect(() => {
        if (accountIds.prodAccountId) {
            getCddCases(accountIds.prodAccountId, enableAccountCddListParams);
        }
    }, [accountIds.prodAccountId, getCddCases]);
    useEffect(() => {
        if (accountIds.prodAccountId) {
            getPayoutAccountConfig(accountIds.prodAccountId);
        }
    }, [accountIds.prodAccountId, getPayoutAccountConfig]);

    const { t } = useTranslation();

    if (!account.livemode || loading) {
        return null;
    }

    if (hasPayout) {
        return <Card marginBottom={distances.normal}>
            <H2>{t('settings.account.payout.title')}</H2>
            <Hr />
            <BankAccounts
                organizationNumber={account.company.organization_number!}
                accountId={accountIds.optimisticProdAccountId}
            />
            {/**
             * TODO: display a helpful text informing that no payout will be performed until
             * a gateway is configured for payout (if there no payment gateways are configured for payout)
             */}
        </Card>;
    }

    return null;
};

export default PayoutInfo;

const Center = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: ${distances.normal};
`;
